import { router } from 'expo-router';
import { useCallback, useRef, useState } from 'react';

import { useRedeemPromoCodeV2Mutation } from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { createRbiApplyPromoCodeEvent } from '@fhs-legacy/frontend/src/pages/loyalty/loyalty-offers/loyalty-promo-code/utils';
import { useCRMEventsContext } from '@fhs-legacy/frontend/src/state/crm-events';
import { routes } from '@fhs-legacy/frontend/src/utils/routing';

import { useLoyaltyLegacyStates } from '../../hooks/use-loyalty-legacy-states';
import { useLoyaltyIncentives } from '../../queries/loyalty.queries';

export enum PromoCodeErrorReason {
  redeemedCode = 'REDEEMED_CODE',
  expiredCode = 'EXPIRED_CODE',
  invalidCode = 'INVALID_CODE',
}

export class PromoCodeError extends Error {
  public message: PromoCodeErrorReason;

  constructor(message: PromoCodeErrorReason) {
    super();
    this.message = message;
  }
}

export const useRedeemPromoCode = ({ onDismiss }: { onDismiss: () => void }) => {
  const { logRBIEvent } = useCRMEventsContext();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isRedeeming, setIsRedeeming] = useState<boolean>(false);
  const promoCodeOfferId = useRef('');
  const [redeemMutation] = useRedeemPromoCodeV2Mutation();
  const { loyaltyId } = useLoyaltyLegacyStates();
  const { refetch } = useLoyaltyIncentives();
  const redeemPromoCode = useCallback(
    async (promoCode: string) => {
      let event = createRbiApplyPromoCodeEvent(promoCode, 'Successful');
      try {
        setIsRedeeming(true);
        const { data } = await redeemMutation({
          variables: {
            input: {
              loyaltyId,
              code: promoCode,
              shouldRedeem: true,
            },
          },
        });
        const configId = data?.loyaltyValidatePromoCodeV2?.configId;
        const personalizedOfferId = data?.loyaltyValidatePromoCodeV2?.personalizedOfferId;

        if (!configId || !personalizedOfferId) {
          const reason = data?.loyaltyValidatePromoCodeV2?.reason;
          const error =
            (reason && PromoCodeErrorReason[reason]) || PromoCodeErrorReason.invalidCode;

          throw new PromoCodeError(error);
        }
        promoCodeOfferId.current = personalizedOfferId;
        onDismiss();
        await refetch();
        setTimeout(() => {
          router.navigate(`${routes.offersV2Details}/${promoCodeOfferId.current}`);
        }, 1000);
      } catch (err) {
        const error =
          err instanceof PromoCodeError
            ? err
            : new PromoCodeError(PromoCodeErrorReason.invalidCode);
        event = createRbiApplyPromoCodeEvent(promoCode, 'Failed', error.message);

        switch (error.message) {
          case PromoCodeErrorReason.expiredCode:
            setErrorMessage('This promo code is expired. Please enter a valid one.');
            break;
          case PromoCodeErrorReason.redeemedCode:
            setErrorMessage('Promo code already redeemed. Please enter a valid one.');
            break;
          case PromoCodeErrorReason.invalidCode:
          default:
            setErrorMessage('Please enter a valid promo code.');
        }
      } finally {
        setIsRedeeming(false);
        logRBIEvent(event);
      }
    },
    [loyaltyId, onDismiss, redeemMutation, refetch]
  );
  return { redeemPromoCode, errorMessage, isRedeeming };
};
